import { Component, ChangeDetectionStrategy, input, output, forwardRef, computed } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ChipModule, ChipPassThrough } from 'primeng/chip';
import { TranslocoModule } from '@jsverse/transloco';
import { Amenity, AmenityCategory, AmenityGroup } from '../../../models';

/** Kolejność wyświetlania kategorii */
const CATEGORY_ORDER: AmenityCategory[] = [
  'general',
  'bathroom',
  'kitchen',
  'bedroom',
  'outdoor',
  'entertainment',
  'safety',
  'accessibility',
  'parking',
  'services'
];

@Component({
  selector: 'app-amenity-selector',
  standalone: true,
  imports: [ChipModule, TranslocoModule],
  templateUrl: './amenity-selector.component.html',
  styleUrl: './amenity-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AmenitySelectorComponent),
      multi: true
    }
  ]
})
export class AmenitySelectorComponent implements ControlValueAccessor {
  readonly amenities = input.required<Amenity[]>();
  readonly label = input<string>('Udogodnienia');
  readonly disabled = input<boolean>(false);

  readonly selectionChange = output<number[]>();

  selectedIds: number[] = [];

  /** Amenities pogrupowane po kategorii */
  readonly groupedAmenities = computed<AmenityGroup[]>(() => {
    const amenities = this.amenities();
    const grouped = new Map<AmenityCategory, Amenity[]>();

    for (const amenity of amenities) {
      const list = grouped.get(amenity.category) || [];
      list.push(amenity);
      grouped.set(amenity.category, list);
    }

    return CATEGORY_ORDER
      .filter(cat => grouped.has(cat))
      .map(cat => ({ category: cat, amenities: grouped.get(cat)! }));
  });

  /** Design tokens dla niezaznaczonego chipa - używa globalnych tokenów z app.config.ts */
  readonly defaultChipDt = {
    background: 'var(--p-surface-0)',
    color: 'var(--p-surface-700)',
    icon: {
      color: 'var(--p-surface-500)'
    }
  };

  /** Design tokens dla zaznaczonego chipa - subtelne tło jak w designie */
  readonly selectedChipDt = {
    background: 'color-mix(in srgb, var(--p-primary-color) 12%, var(--p-surface-0))',
    color: 'var(--p-primary-700, var(--p-primary-color))',
    icon: {
      color: 'var(--p-primary-color)'
    }
  };

  /** PassThrough dla niezaznaczonego chipa - dodaje border i kompaktowe paddingi */
  readonly defaultChipPt: ChipPassThrough = {
    root: {
      style: {
        border: '1px solid var(--p-surface-300)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        padding: '0.375rem 0.75rem',
        borderRadius: '1.5rem',
        fontSize: '0.875rem'
      }
    },
    icon: {
      style: {
        fontSize: '0.75rem'
      }
    }
  };

  /** PassThrough dla zaznaczonego chipa - primary border i kompaktowe paddingi */
  readonly selectedChipPt: ChipPassThrough = {
    root: {
      style: {
        border: '1px solid var(--p-primary-color)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        padding: '0.375rem 0.75rem',
        borderRadius: '1.5rem',
        fontSize: '0.875rem'
      }
    },
    icon: {
      style: {
        fontSize: '0.75rem'
      }
    }
  };

  getChipDt(id: number): object {
    return this.isSelected(id) ? this.selectedChipDt : this.defaultChipDt;
  }

  getChipPt(id: number): ChipPassThrough {
    return this.isSelected(id) ? this.selectedChipPt : this.defaultChipPt;
  }

  getChipIcon(id: number): string {
    return this.isSelected(id) ? 'pi pi-check' : 'pi pi-plus';
  }

  private onChange: (value: number[]) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: number[]): void {
    this.selectedIds = value || [];
  }

  registerOnChange(fn: (value: number[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // handled via input
  }

  isSelected(id: number): boolean {
    return this.selectedIds.includes(id);
  }

  toggle(id: number): void {
    if (this.disabled()) return;

    const index = this.selectedIds.indexOf(id);
    if (index === -1) {
      this.selectedIds = [...this.selectedIds, id];
    } else {
      this.selectedIds = this.selectedIds.filter(i => i !== id);
    }
    this.onChange(this.selectedIds);
    this.onTouched();
    this.selectionChange.emit(this.selectedIds);
  }
}
